/**
 * Common utility functions shared between skupreme-client and skupreme-warehouse-dashboard
 * These functions are 100% identical in both projects
 */

/**
 * Check if a value is a string
 */
export function isString(x: unknown): x is string {
  return Object.prototype.toString.call(x) === '[object String]';
}

/**
 * Check if a value is not empty (not undefined, null, empty string, or empty array)
 */
export function isNotEmpty(item: unknown): boolean {
  return (
    item !== undefined &&
    item !== null &&
    item !== '' &&
    (item as any).length !== 0
  );
}

/**
 * Check if a value is not null or undefined
 */
export function isNotNull(item: unknown): boolean {
  return item !== undefined && item !== null;
}

/**
 * Format a number with comma separators (e.g., 1000000 → "1,000,000")
 */
export function numberWithCommas(
  x: number | string | null | undefined,
): string {
  return x?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') ?? '';
}

/**
 * Format a decimal number with commas and specified decimal places
 */
export function decimalWithCommas(x: number, decimal: number = 2): string {
  if (Number.isNaN(x)) {
    return '-';
  }
  if (decimal) {
    const decimalValue =
      Math.round((x + Number.EPSILON) * 10 ** decimal) / 10 ** decimal;
    const formatted = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: decimal,
    }).format(decimalValue);
    return formatted;
  }
  return x?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/**
 * Copy text to clipboard
 */
export function copyToClipboard(text: string): void {
  const textArea = document.createElement('textarea');
  textArea.value = text;
  document.body.appendChild(textArea);
  textArea.select();
  document.execCommand('copy');
  textArea.remove();
}

/**
 * Check if an array is valid (not null, not undefined, is array, has items)
 */
export function isValidArray<T>(data: T[] | null | undefined): data is T[] {
  return (
    data !== null &&
    data !== undefined &&
    Array.isArray(data) &&
    data.length > 0
  );
}

/**
 * Get a valid array or empty array if invalid
 */
export function getValidArray<T>(data: T[] | null | undefined): T[] {
  return isValidArray(data) ? data : [];
}

/**
 * Check if a value is a valid number
 */
export function isValidNumber(data: unknown): boolean {
  return !!data && !Number.isNaN(Number(data));
}

/**
 * Check if an object is valid (not null, is object, has keys)
 */
export function isValidObject<T extends Record<string, unknown>>(
  data: T | null | undefined,
): data is T {
  return (
    typeof data === 'object' && data !== null && Object.keys(data).length > 0
  );
}

/**
 * Generate a random ID string
 */
export function generateRandomId(): string {
  return Math.random().toString(36).slice(2, 11);
}

/**
 * Sort an array of objects by a specific key
 */
export function sortArrayObjectsByKey<T extends Record<string, unknown>>(
  data: T[] | null | undefined,
  key: keyof T,
): T[] {
  if (!isValidArray(data)) return [];
  return [...data].sort((a, b) => `${a[key]}`.localeCompare(`${b[key]}`));
}

/**
 * Sort dropdown options by their text property
 */
export function sortDropdownOptions<T extends { text: string }>(
  data: T[],
): T[] {
  return sortArrayObjectsByKey(data, 'text');
}

/**
 * Round a number to specified decimal places
 */
export function roundNumber(
  num: number | string | null | undefined,
  decimal: number = 2,
): number {
  const parsed = Number(num);
  return !isNaN(parsed) && num
    ? Math.round((parsed + Number.EPSILON) * 10 ** decimal) / 10 ** decimal
    : 0;
}

/**
 * Get CDN link for a given path
 */
export function getCdnLink(path: string): string {
  return `https://cdn.skupreme.com/public/${path}`;
}

/**
 * Try to parse a JSON string, returns false if parsing fails
 */
export function tryParseJSONObject<T = unknown>(jsonString: string): T | false {
  try {
    const obj = JSON.parse(jsonString);
    return obj && typeof obj === 'object' ? (obj as T) : false;
  } catch {
    return false;
  }
}

/**
 * Check if a value is a valid date
 */
export function isValidDate(date: unknown): boolean {
  if (!date) return false;

  try {
    if (typeof date === 'string') {
      // Ensure the string is not purely numeric
      if (!isNaN(Number(date))) return false;
    }

    if (typeof date === 'string' || date instanceof Date) {
      const parsedDate = new Date(date);
      return !isNaN(parsedDate.getTime());
    }

    return false;
  } catch {
    return false;
  }
}

/**
 * Convert an object to URL query parameters
 */
export function objectToParams(obj: Record<string, unknown>): string {
  return Object.entries(obj)
    .filter(([, v]) => v !== undefined && v !== null)
    .map(
      ([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`,
    )
    .join('&');
}

/**
 * Remove underscore/hyphen and capitalize each word
 */
export function removeUnderscoreAndCapitalize(str: unknown): string {
  if (!isString(str)) return '';
  return str
    .split(/[_-]+/)
    .map(
      (word: string) =>
        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
    )
    .join(' ');
}

/**
 * Convert string to Title Case
 */
export function toTitleCase(phrase: string | null | undefined): string {
  return (phrase || '')
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Capitalize first letter of a string
 */
export function toCapitalize(phrase: string | null | undefined): string {
  return typeof phrase === 'string' && phrase
    ? phrase.charAt(0).toUpperCase() + phrase.slice(1)
    : '';
}

/**
 * Download data as CSV file
 */
export function downloadCSV(data: BlobPart, fileName: string): void {
  const blob = new Blob([data], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.href = url;
  link.download = fileName.endsWith('.csv') ? fileName : `${fileName}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Download a Blob as PDF
 */
export function downloadPDF(data: Blob | MediaSource, fileName: string): void {
  const url = window.URL.createObjectURL(data);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

/**
 * Promise-based timeout
 */
export function timeoutPromise(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Truncate a string with ellipsis
 */
export function truncateString(
  text: string | null | undefined,
  maxLength: number,
): string {
  return (text || '').length > maxLength
    ? `${text!.substring(0, maxLength)}...`
    : text || '';
}

/**
 * Compare two strings for search (case-insensitive)
 */
export function searchQueryCompare(a: string, b: string): boolean {
  if (isString(a) && isString(b)) {
    return a.toLowerCase().includes(b.toLowerCase());
  }
  return false;
}

/**
 * Check if a value is a boolean
 */
export function isBoolean(value: unknown): value is boolean {
  return value === true || value === false;
}

/**
 * Reorder items in a list (for drag and drop)
 */
export function reorderList<T>(
  list: T[],
  startIndex: number,
  endIndex: number,
): T[] {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
}

/**
 * Remove duplicate objects from array by key
 */
export function removeDuplicatesByKey<T extends Record<string, unknown>>(
  array: T[],
  key: keyof T,
): T[] {
  return Array.from(
    array
      .reduce((map, obj) => map.set(obj[key], obj), new Map<unknown, T>())
      .values(),
  );
}

/**
 * Convert base64 string to Blob
 */
export function base64ToBlob(
  base64: string,
  mimeType: string = 'image/png',
): Blob {
  const byteString = atob(base64.split(',')[1]);
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);

  for (let i = 0; i < byteString.length; i += 1) {
    ia[i] = byteString.charCodeAt(i);
  }
  return new Blob([ab], { type: mimeType });
}

/**
 * Get a fixed number with specified decimal places
 */
export function getFixNumber(
  value: number | null | undefined,
  digit: number = 2,
): number | null {
  if (!Number.isNaN(value) && value !== null && value !== undefined) {
    const rule = 10 ** digit;
    return Math.round(value * rule) / rule;
  }
  return null;
}

/**
 * Sort an object by its keys
 */
export function sortObject<T extends Record<string, unknown>>(obj: T): T {
  return Object.keys(obj)
    .sort()
    .reduce((result, key) => {
      (result as Record<string, unknown>)[key] = obj[key];
      return result;
    }, {} as T);
}

/**
 * Get cookie value by name
 */
export function getCookie(name: string): string {
  const namePrefix = `${name}=`;
  const decodedCookie = decodeURIComponent(document.cookie);
  const cookieArray = decodedCookie.split(';');

  for (const cookie of cookieArray) {
    const trimmed = cookie.trim();
    if (trimmed.indexOf(namePrefix) === 0) {
      return trimmed.substring(namePrefix.length);
    }
  }

  return '';
}

/**
 * Mask account number (show only last 4 digits)
 */
export function maskAccountNumber(
  accountNumber: string | unknown,
): string | unknown {
  if (typeof accountNumber === 'string' && accountNumber.length > 4) {
    const visibleChars = 4;
    const masked = '*'.repeat(accountNumber.length - visibleChars);
    return masked + accountNumber.slice(-visibleChars);
  }
  return accountNumber;
}
